/**
 * Brief vtw-ih-02/2026-10-30-006: catches a failed lazy-page load (stale chunk after a
 * deploy). First failure: one automatic reload (utils/chunkReload). A failure again inside the
 * guard window: a message with a reload button, Lao by default. Any other error is re-thrown,
 * never swallowed.
 */
import { Component, useContext, type ErrorInfo, type ReactNode } from 'react';
import { LanguageContext } from '../../contexts/LanguageContext';
import { isChunkLoadError, isChunkReloadPending, reloadOnceForChunkError } from '../../utils/chunkReload';

export const CHUNK_ERROR_TEXT = {
  lo: {
    title: 'ມີເວີຊັນໃໝ່ຂອງແອັບ',
    body: 'ໜ້ານີ້ໂຫຼດບໍ່ສຳເລັດ. ກະລຸນາໂຫຼດໜ້າຄືນໃໝ່.',
    reload: 'ໂຫຼດໜ້າຄືນໃໝ່',
  },
  en: {
    title: 'A new version of the app is available',
    body: 'This page could not load. Please reload the page.',
    reload: 'Reload page',
  },
} as const;

export function ChunkErrorMessage() {
  const language = useContext(LanguageContext)?.language ?? 'lo';
  const t = CHUNK_ERROR_TEXT[language] ?? CHUNK_ERROR_TEXT.lo;
  return (
    <div role="alert" lang={language} className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t.title}</p>
      <p className="text-gray-600 dark:text-gray-300">{t.body}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
      >
        {t.reload}
      </button>
    </div>
  );
}

interface Props {
  children: ReactNode;
  /** A change (e.g. the path) clears a shown error. */
  resetKey?: string;
}

interface State {
  error: Error | null;
  resetKey?: string;
  /** null until componentDidCatch decides; true = a reload is under way. */
  reloading: boolean | null;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey, reloading: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, reloading: null };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { resetKey: props.resetKey, error: null, reloading: null };
    }
    return null;
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (!isChunkLoadError(error)) return;
    this.setState({ reloading: reloadOnceForChunkError() });
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (isChunkReloadPending()) return null; // the page is reloading; hold still
    if (!isChunkLoadError(error)) throw error;
    if (reloading === false) return <ChunkErrorMessage />;
    return null; // deciding, or the reload is under way
  }
}

export default ChunkErrorBoundary;
